
import React, { useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import GSSVG from 'smartstream-widgets/controls/GSSVG'
import usePreviousValue from 'smartstream-widgets/hooks/usePreviousValue/usePreviousValue'
import { withStyles, createStyles} from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { Props, GetPriceFn, PriceFormat } from './types'
import { getMeasurablePrice } from 'smartstream-widgets/utils/getMeasurablePrice'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

const styles = (theme: Theme) => createStyles({
  price: {
    textAlign: 'center',
    position: 'relative',
    '& > svg': {
      position: 'absolute'
    },
    '& > span': {
      verticalAlign: 'sub',
      display: 'inline-block'
    }
  },
  pricedown: {
    color: theme.palette.error.main,
    transition: 'color .3s ease-in',
    '& + svg': {
      transform: 'rotate(180deg)'
    }
  },
  priceup: {
    color: theme.palette.success.main,
    transition: 'color .3s ease-in-out'
  }
})

export const getFormattedPrice = (
  format: string,
  price: PriceFormat,
  locale = 'en-US',
  precision = 2
): string => {
  let formattedPrice: string
  if (typeof (price) === 'string') return price
  if (!price) return ''
  switch (format) {
    case 'american':
      formattedPrice = `${price.american > 0 ? '+' : ''}${price.american}`
      break
    case 'fraction':
      formattedPrice = `${price.numerator}/${price.denominator}`
      break
    default:
      formattedPrice = new Intl.NumberFormat(locale, { maximumFractionDigits: precision }).format(price.decimal)
  }
  return formattedPrice
}

export const getLabelPrice: GetPriceFn = (format, price, marketType, handicap) => {
  const formattedPrice = getFormattedPrice(format, price)
  return marketType === 'spread' && handicap ? `${handicap > 0 ? '+' : ''}${handicap}(${formattedPrice})` : `${formattedPrice}`
}

export const validateShouldPriceUpdate = (prevPrice, price, setPriceUpdate) => {
  if (prevPrice) {
    prevPrice > price && setPriceUpdate('pricedown')
    prevPrice < price && setPriceUpdate('priceup')
  }
}

export function Price(props: Props): React.ReactElement {
  const { classes, config, marketType, handicap, price, betgeniusFixtureId } = props

  const [priceUpdate, setPriceUpdate] = useState('')
  const arrowIcon = `${config.BASE_ASSET_PATH}/img/icons.svg#ic-arrow-upward`
  const measurablePrice = getMeasurablePrice(price)
  const prevPrice: number | null = usePreviousValue(measurablePrice)
  const prevMarketType: string | null = usePreviousValue(marketType)
  const prevFixtureId: string | null = usePreviousValue(betgeniusFixtureId)
  const priceClass: string = priceUpdate !== '' ? classes[priceUpdate] + ' update-' + priceUpdate : ''

  useEffect(() => {
    let timeoutId

    if (prevFixtureId === betgeniusFixtureId && prevMarketType === marketType) {
      validateShouldPriceUpdate(prevPrice, measurablePrice, setPriceUpdate)

      timeoutId = setTimeout(() => {
        setPriceUpdate('')
      }, 2000)
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [price])

  return (
    <div className={classes['price']}>
      <Typography variant='caption' className={priceClass}>
        {getLabelPrice(config.oddsFormat, price, marketType, handicap)}
      </Typography>
      {priceUpdate !== '' &&
        <GSSVG
          className={classes[priceUpdate]}
          xlinkHref={arrowIcon} />
      }
    </div>
  )
}

export default withStyles(styles, {defaultTheme: getDefaultTheme})(Price)
